<template>
    <div class="report-content">
        <div class="graphics">
            <Hide v-model="chartVisibility">
                <h4>{{ $t('reports.callStatistics') }}</h4>
            </Hide>
            <div v-show="chartVisibility">
                <div v-show="numberOfCallsLoaded">
                    <span class="chart-name"> {{ $t('reports.locationReport.callsPerLocation') }} </span>
                    <div class="calls-per-location"></div>
                    <div ref="locationChart0" class="chartist-legend"></div>
                </div>

                <div v-show="callDurationLoaded">
                    <span class="chart-name">
                        {{ $t('reports.locationReport.callDurationPerLocation') }}
                    </span>
                    <div class="call-duration-per-location"></div>
                    <div ref="locationChart1" class="chartist-legend"></div>
                </div>

                <LoadingBlock v-if="chartLoader" />
                <h4 v-if="!chartLoader && !numberOfCallsData && !callDurationData" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>
        </div>

        <div class="detailing">
            <Hide v-model="tableVisibility">
                <h4 ref="details">{{ $t('reports.detailed') }}</h4>
            </Hide>
        </div>
        <div v-show="tableVisibility">
            <Pagination
                v-if="locationLength > 25"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />
            <table
                v-if="!tableLoader && reportData.length > 0"
                id="forAT_table-LocationReport"
                class="common-table"
            >
                <tr>
                    <th>{{ $t('reporting.commonLabels.callType') }}</th>
                    <th>{{ $t('common.location') }}</th>
                    <th>{{ $t('reporting.commonLabels.startTimeDate') }}</th>
                    <th>{{ $t('reporting.commonLabels.endTimeDate') }}</th>
                    <th>{{ $t('reports.locationReport.duration') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(call, index) in reportData" :key="index">
                        <td>
                            <div class="call-type">
                                {{ parseCallTypes(call.categories)[0] }}
                                <span>
                                    <i
                                        v-for="(type, index2) in parseCallTypes(call.categories)"
                                        :key="index2"
                                        :class="'icon-' + type.toLocaleLowerCase()"
                                    ></i>
                                </span>
                            </div>
                        </td>
                        <td class="location">{{ call.location }}</td>
                        <td>
                            <div class="date">{{ call.startDateFormatted }}</div>
                            <div class="date">{{ call.startTimeFormatted }}</div>
                        </td>
                        <td>
                            <div v-if="!call.isUnfinished" class="date">
                                {{ call.endDateFormatted }}
                            </div>
                            <div v-if="!call.isUnfinished" class="date">
                                {{ call.endTimeFormatted }}
                            </div>
                            <div v-if="call.isUnfinished">{{ call.unfinishedText }}</div>
                        </td>

                        <td>{{ call.diffDates }}</td>
                    </tr>
                </tbody>
            </table>
            <LoadingBlock v-if="tableLoader" />
            <Pagination
                v-if="locationLength > 25"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />
            <h4 v-if="!tableLoader && locationLength === 0 && reportData.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>
    </div>
</template>

<script type="text/babel">
import Pagination from '../../common/Pagination.vue';
import Hide from '../../common/Hide.vue';
import parseISO from 'date-fns/parseISO';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import {
    resourceLocationReport,
    resourceLocationNumberOfCalls,
    resourceAverageCallDuration,
    resourceLocationLength,
} from '../../../api/resources';
import { getCancelToken } from '../../../api';
import LoadingBlock from '../../common/LoadingBlock';
import reportingUtils from '../../common/reportingUtils';
import {
    horizontalBarBuilder,
    flipThePage,
    isUnfinished,
    getUnfinishedText,
    toCachePages,
} from '../helpers/reportsHelper';
import isEqual from 'lodash-es/isEqual';
import {
    formatDateString,
    getTimeString,
    doScroll,
    secsToReadableTime,
    getDateFormat,
} from '../../common/helpers';
import { mapState, mapActions } from 'vuex';
import { ERROR } from '../../../constants';

export default {
    components: {
        Pagination,
        Hide,
        LoadingBlock,
    },

    mixins: [reportingUtils],

    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            reportData: [],
            numberOfCallsData: undefined,
            callDurationData: undefined,
            locationLength: 0,
            cache: {},
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: 25,
            },
            chartLoader: true,
            tableLoader: true,
            chartVisibility: true,
            tableVisibility: true,
            cancelToken: getCancelToken(),
            cancelPaginationToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['callTypes']),

        /**
         * Number of calls chart loading status
         * @returns {Boolean}
         */
        numberOfCallsLoaded() {
            return !this.chartLoader && !!this.numberOfCallsData;
        },

        /**
         * Call duration per location chart loading status
         * @returns {Boolean}
         */
        callDurationLoaded() {
            return !this.chartLoader && !!this.callDurationData;
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
                this.loadReport();
            }
        },

        'pagination.current_page'() {
            doScroll(this.$refs.details);
        },

        chartVisibility(value) {
            if (value) {
                if (this.numberOfCallsData) {
                    horizontalBarBuilder(
                        this.numberOfCallsData,
                        this.$refs.locationChart0,
                        '.calls-per-location',
                        this.callTypes,
                        false
                    );
                }
                if (this.callDurationData) {
                    horizontalBarBuilder(
                        this.callDurationData,
                        this.$refs.locationChart1,
                        '.call-duration-per-location',
                        this.callTypes,
                        true
                    );
                }
            }
        },
    },

    mounted() {
        this.loadReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        this.cancelPaginationToken.cancel();
    },

    methods: {
        toCachePages,

        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * Load report data. In case of error will be displayed popup message with text.
         */
        async loadReport() {
            try {
                await Promise.all([
                    this.loadNumberOfCalls(),
                    this.loadAverageCallDuration(),
                    this.loadLocationLengthAndTableData(),
                ]);
            } catch (error) {
                this.tableLoader = false;
                this.chartLoader = false;
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            }
        },

        /**
         * Loading data for Number of calls chart. Initializing horizontalBarBuilder function if data is not null
         * for building chart.
         */
        async loadNumberOfCalls() {
            this.numberOfCallsData = (
                await resourceLocationNumberOfCalls({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            this.chartLoader = false;
            if (this.numberOfCallsData) {
                horizontalBarBuilder(
                    this.numberOfCallsData,
                    this.$refs.locationChart0,
                    '.calls-per-location',
                    this.callTypes,
                    false
                );
            }
        },

        /**
         * Loading data for Call duration per location of calls chart. Initializing horizontalBarBuilder function if
         * data is not null for building chart.
         */
        async loadAverageCallDuration() {
            this.callDurationData = (
                await resourceAverageCallDuration({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            this.chartLoader = false;
            if (this.callDurationData) {
                horizontalBarBuilder(
                    this.callDurationData,
                    this.$refs.locationChart1,
                    '.call-duration-per-location',
                    this.callTypes,
                    true
                );
            }
        },

        /**
         * Getting count of records in the table. If value > 0 then will be initialized loadTableData function.
         */
        async loadLocationLengthAndTableData() {
            this.locationLength = (
                await resourceLocationLength({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            if (this.locationLength) {
                this.pagination.last_page = Math.ceil(this.locationLength / this.pagination.per_page);
                this.loadTableData();
            } else {
                this.tableLoader = false;
            }
        },

        handlePaginationCallback() {
            this.tableLoader = false;
            flipThePage.call(this);
        },

        /**
         * Loading table data depends on pagination parameters. By default, the data is loaded from the 0 page.
         */
        loadTableData() {
            this.tableLoader = true;
            let data = {
                ...this.reportParameters,
                limit: 25,
                offset: (this.pagination.current_page - 1) * this.pagination.per_page,
            };

            return resourceLocationReport({ data, cancelToken: this.cancelPaginationToken.token })
                .then(({ data }) => {
                    let newData = data.map(call => ({
                        ...call,
                        startDateFormatted: formatDateString(call.start_time, getDateFormat()),
                        startTimeFormatted: getTimeString(call.start_time),
                        endDateFormatted: formatDateString(call.end_time, getDateFormat()),
                        endTimeFormatted: getTimeString(call.end_time),
                        isUnfinished: isUnfinished(call),
                        unfinishedText: getUnfinishedText(call),
                        diffDates: isUnfinished(call)
                            ? ''
                            : this.getDiffDates(parseISO(call.start_time), parseISO(call.end_time)),
                    }));
                    this.reportData = newData;
                    this.cache[this.pagination.current_page] = newData;
                })
                .catch(response => {
                    // TODO Handler error (https://jira.ascom-ws.com/browse/MERA-1546)
                    return response;
                })
                .then(response => {
                    if (response !== 'canceled') {
                        this.tableLoader = false;
                    }
                });
        },

        /**
         * @param {date} start
         * @param {date} end
         * @return {number|string} in milliseconds or string in HH:mm:ss or mm:ss format
         */
        getDiffDates(start, end) {
            if (start instanceof Date && end instanceof Date) {
                let ms = differenceInMilliseconds(end, start);
                return secsToReadableTime(ms / 1000);
            }

            return '';
        },

        resetToDefault() {
            this.reportData = [];
            this.numberOfCallsData = undefined;
            this.callDurationData = undefined;
            this.locationLength = 0;
            this.cache = {};
            this.pagination = {
                current_page: 1,
                last_page: 0,
                per_page: 25,
                offset: 2,
            };
            if (this.$refs.locationChart0 && this.$refs.locationChart1) {
                this.$refs.locationChart0.innerHTML = '';
                this.$refs.locationChart1.innerHTML = '';
            }
        },
    },
};
</script>
